import { ChangeEvent, KeyboardEvent, useId } from 'react';

export type SliderInputProps = {
    label: string;
    suffix: string;
    min: number;
    max: number;
    value: number | null;
    onChange: (value: number | null) => void;
    formatNumber: (n: number) => string;
    defaultStep?: number;
    rangeDescription: string;
};

export const SliderInput = ({ label, suffix, value, onChange, min, max, formatNumber, defaultStep = 1, rangeDescription }: SliderInputProps) => {
    const inputId = useId();
    const labelId = useId();
    const suffixId = useId();
    const rangeId = useId();
    const stripSpaces = (str: string) => str.replace(/\s/g, '');

    const updateValue = ({ target }: ChangeEvent<HTMLInputElement>) => {
        const raw = stripSpaces(target.value);

        if (raw === '') {
            onChange(null);
            return;
        }
        const num = parseInt(raw, 10);
        if (!isNaN(num)) {
            onChange(num);
        }
    };

    const onBlur = ({ target }: ChangeEvent<HTMLInputElement>) => {
        const raw = stripSpaces(target.value);

        if (!target?.value) {
            onChange(min);
        }

        const value = parseInt(raw, 10);
        if (value < min) {
            onChange(min);
        }
        if (value > max) {
            onChange(max);
        }
    };

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        const step = e.shiftKey ? 10 * defaultStep : 1 * defaultStep;
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            const newValue = Math.min((value || 0) + step, max);
            onChange(newValue);
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            const newValue = Math.max((value || 0) - step, min);
            onChange(newValue);
        }
    };

    return (
        <div className="r-mb-6 r-flex r-items-center r-justify-between">
            <label id={labelId} htmlFor={inputId}>
                {label}
            </label>
            <div className="r-relative">
                <input
                    id={inputId}
                    value={formatNumber(value || 0)}
                    type="text"
                    inputMode="numeric"
                    aria-labelledby={`${labelId} ${suffixId}`}
                    aria-describedby={rangeId}
                    onBlur={onBlur}
                    onChange={updateValue}
                    onKeyDown={onKeyDown}
                    className="r-h-[56px] r-w-[128px] r-rounded-2xl r-border r-border-[#adb5bd] r-pr-8 r-text-end r-text-lg after:r-mr-5 after:r-content-['kr']"
                />
                <span id={suffixId} className="r-absolute r-right-3 r-top-1/2 r--translate-y-1/2">
                    {suffix}
                </span>
                <span id={rangeId} className="r-sr-only">
                    {rangeDescription}
                </span>
            </div>
        </div>
    );
};
